<div class="app-main__inner">
  <div class="bb-settings-wrap">
    <div class="card bb-settings-card">

      <!-- ── Card Header ─────────────────────────────────────────── -->
      <div class="card-header lc-card-header">
        <span class="lc-card-title">
          <i class="pe-7s-print text-primary"></i>
          Label Settings
          <span *ngIf="isDirty && !isSaving" class="lc-unsaved-badge">Unsaved</span>
        </span>
        <div class="lc-header-actions">
          <button type="button" class="btn btn-sm lc-btn-ghost" (click)="loadLayouts()"
                  [disabled]="isLoading || isSaving" title="Reload">
            <i class="pe-7s-refresh"></i>
          </button>
          <span class="lc-default-badge" *ngIf="!isNew && isCurrentDefault">★ Default</span>
          <button type="button" class="btn btn-sm btn-outline-warning"
                  *ngIf="!isNew && !isCurrentDefault"
                  (click)="setAsDefault()" [disabled]="isSaving">
            ★ Set Default
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger"
                  (click)="deleteLayout()" [disabled]="!canDelete || isSaving"
                  title="{{ !canDelete ? 'Cannot delete — at least one layout must remain' : 'Delete this layout' }}">
            <i class="pe-7s-trash"></i>
          </button>
          <button type="button" class="btn btn-sm btn-primary"
                  (click)="saveLayout()" [disabled]="isSaving || (!isDirty && !isNew)">
            <span *ngIf="isSaving" class="spinner-border spinner-border-sm me-1"></span>
            <i *ngIf="!isSaving" class="pe-7s-diskette me-1"></i>
            {{ isSaving ? 'Saving…' : (isNew ? 'Save New' : 'Save Changes') }}
          </button>
        </div>
      </div>

      <!-- ── Card Body ───────────────────────────────────────────── -->
      <div class="card-body">

        <!-- Loading -->
        <div class="lc-loading" *ngIf="isLoading">
          <div class="spinner-border text-primary spinner-border-sm"></div>
          <span>Loading layouts…</span>
        </div>

        <ng-container *ngIf="!isLoading">

          <!-- ── Layout Selector Bar ──────────────────────────────── -->
          <div class="lc-selector-bar">
            <div class="lc-selector-bar__left">
              <span class="lc-bar-label">Layout</span>
              <select class="form-select form-select-sm lc-layout-select"
                      [ngModel]="isNew ? -1 : activeIndex"
                      (ngModelChange)="onLayoutSelect($event)">
                <option [value]="-1" *ngIf="isNew">— New Layout —</option>
                <option *ngFor="let l of layouts; let i = index" [value]="i">
                  {{ l.isDefault ? '★ ' : '' }}{{ l.name }} ·
                  {{ l.pageSize === 'A4' ? 'A4' : (l.pageSize === 'thermal' ? 'Thermal' : 'Die-cut') }}
                  {{ l.labelWidth }}mm
                </option>
              </select>
            </div>
            <button type="button" class="btn btn-sm btn-outline-primary"
                    (click)="startNew()" [disabled]="isNew">
              <i class="pe-7s-plus me-1"></i>New Layout
            </button>
          </div>

          <!-- ── Layout Name Bar ──────────────────────────────────── -->
          <div class="lc-name-bar">
            <span class="lc-bar-label">Name</span>
            <div class="lc-name-bar__field">
              <input type="text" class="form-control form-control-sm lc-name-input"
                     [(ngModel)]="editing.name"
                     (ngModelChange)="nameError = ''"
                     placeholder="e.g. A4 Standard, Thermal 58mm…"
                     [class.is-invalid]="!!nameError">
              <div class="invalid-feedback" *ngIf="nameError">{{ nameError }}</div>
            </div>
            <div class="form-check lc-default-check" *ngIf="isNew">
              <input class="form-check-input" type="checkbox" id="markAsDefault" [(ngModel)]="markAsDefault">
              <label class="form-check-label" for="markAsDefault">Set as default</label>
            </div>
          </div>

          <!-- ── Two-panel content ────────────────────────────────── -->
          <div class="lc-content">

            <!-- ── Form column ──────────────────────────────────── -->
            <div class="lc-form">

              <!-- Quick Presets -->
              <div class="lc-section">
                <div class="lc-section-header">Quick Presets</div>
                <div class="lc-presets">
                  <button type="button" *ngFor="let p of presets"
                          class="btn btn-sm lc-preset-btn"
                          [class.active]="editing.printerType === (p.cfg.printerType || 'browser') && editing.pageSize === p.cfg.pageSize && editing.labelWidth === p.cfg.labelWidth && (!p.cfg.labelHeight || editing.labelHeight === p.cfg.labelHeight)"
                          (click)="applyPreset(p)">
                    {{ p.label }}
                  </button>
                </div>
              </div>

              <!-- Printer Driver -->
              <div class="lc-section">
                <div class="lc-section-header">Printer Driver</div>
                <div class="btn-group w-100" role="group">
                  <button type="button" class="btn btn-outline-primary btn-sm lc-toggle-btn"
                          [class.active]="editing.printerType !== 'zebra'"
                          (click)="editing.printerType = 'browser'">
                    <i class="pe-7s-browser me-1"></i>Browser Print
                  </button>
                  <button type="button" class="btn btn-outline-primary btn-sm lc-toggle-btn"
                          [class.active]="editing.printerType === 'zebra'"
                          (click)="onSetZebra()">
                    <i class="pe-7s-print me-1"></i>Zebra ZPL
                  </button>
                </div>
                <p class="lc-hint mt-2">
                  <ng-container *ngIf="editing.printerType !== 'zebra'">
                    Uses browser print dialog — works with any printer.
                  </ng-container>
                  <ng-container *ngIf="editing.printerType === 'zebra'">
                    Sends ZPL to Zebra via Browser Print. Falls back to downloading a <code>.zpl</code> file.
                  </ng-container>
                </p>

                <!-- Page / Media Type — Browser only -->
                <ng-container *ngIf="editing.printerType !== 'zebra'">
                  <div class="lc-subsection-label mt-3">Page / Media Type</div>
                  <div class="btn-group w-100" role="group">
                    <button type="button" class="btn btn-outline-primary btn-sm lc-toggle-btn"
                            [class.active]="editing.pageSize === 'A4'"
                            (click)="editing.pageSize = 'A4'">
                      <i class="pe-7s-copy-file me-1"></i>A4 Sheet
                    </button>
                    <button type="button" class="btn btn-outline-primary btn-sm lc-toggle-btn"
                            [class.active]="editing.pageSize === 'thermal'"
                            (click)="editing.pageSize = 'thermal'">
                      <i class="pe-7s-print me-1"></i>Thermal Roll
                    </button>
                    <button type="button" class="btn btn-outline-primary btn-sm lc-toggle-btn"
                            [class.active]="editing.pageSize === 'die-cut'"
                            (click)="editing.pageSize = 'die-cut'">
                      <i class="pe-7s-scissors me-1"></i>Die-cut Roll
                    </button>
                  </div>
                  <p class="lc-hint mt-1">
                    <ng-container *ngIf="editing.pageSize === 'A4'">Labels in a grid on A4 paper — any laser or inkjet.</ng-container>
                    <ng-container *ngIf="editing.pageSize === 'thermal'">Continuous roll — labels flow top to bottom.</ng-container>
                    <ng-container *ngIf="editing.pageSize === 'die-cut'">Pre-cut labels — each label is a separate page.</ng-container>
                  </p>
                </ng-container>
              </div>

              <!-- ZPL Settings — Zebra only -->
              <ng-container *ngIf="editing.printerType === 'zebra'">
                <div class="lc-section">
                  <div class="lc-section-header">ZPL Settings</div>

                  <!-- DPI -->
                  <div class="lc-inline-group">
                    <span class="lc-inline-label">Printer DPI</span>
                    <div class="btn-group" role="group">
                      <button type="button" class="btn btn-outline-secondary btn-sm lc-toggle-btn"
                              [class.active]="editing.dpi !== 300"
                              (click)="editing.dpi = 203">203 DPI</button>
                      <button type="button" class="btn btn-outline-secondary btn-sm lc-toggle-btn"
                              [class.active]="editing.dpi === 300"
                              (click)="editing.dpi = 300">300 DPI</button>
                    </div>
                  </div>
                  <p class="lc-hint mb-3">ZD230 → 203 DPI. Check your printer spec sheet.</p>

                  <!-- Media Sensing -->
                  <div class="lc-subsection-label">Media / Sensing Type</div>
                  <div class="btn-group w-100" role="group">
                    <button type="button" class="btn btn-outline-secondary btn-sm lc-toggle-btn"
                            [class.active]="(editing.mediaType || 'Y') === 'Y'"
                            (click)="editing.mediaType = 'Y'">Gap / Notch</button>
                    <button type="button" class="btn btn-outline-secondary btn-sm lc-toggle-btn"
                            [class.active]="editing.mediaType === 'N'"
                            (click)="editing.mediaType = 'N'">Continuous</button>
                    <button type="button" class="btn btn-outline-secondary btn-sm lc-toggle-btn"
                            [class.active]="editing.mediaType === 'M'"
                            (click)="editing.mediaType = 'M'">Black Mark</button>
                    <button type="button" class="btn btn-outline-secondary btn-sm lc-toggle-btn"
                            [class.active]="editing.mediaType === 'W'"
                            (click)="editing.mediaType = 'W'">Web / Notch</button>
                  </div>
                  <p class="lc-hint mb-3">
                    <ng-container *ngIf="(editing.mediaType || 'Y') === 'Y'">Standard die-cut roll with gap.</ng-container>
                    <ng-container *ngIf="editing.mediaType === 'N'">Continuous roll — no gap sensing.</ng-container>
                    <ng-container *ngIf="editing.mediaType === 'M'">Black mark on label backing.</ng-container>
                    <ng-container *ngIf="editing.mediaType === 'W'">Perforated or web-notched media.</ng-container>
                  </p>

                  <!-- Print Darkness -->
                  <div class="lc-slider-row">
                    <div class="lc-slider-label">
                      <span>Print Darkness</span>
                      <strong class="lc-slider-value">{{ editing.darkness ?? 10 }} / 30</strong>
                    </div>
                    <input type="range" class="form-range" min="0" max="30" step="1"
                           [ngModel]="editing.darkness ?? 10"
                           (ngModelChange)="editing.darkness = $event">
                    <div class="lc-slider-bounds"><span>0 (lightest)</span><span>30 (darkest)</span></div>
                  </div>

                  <!-- Print Speed -->
                  <div class="lc-inline-group mt-3">
                    <span class="lc-inline-label">Print Speed</span>
                    <div class="btn-group" role="group">
                      <button type="button" *ngFor="let s of speedOptions"
                              class="btn btn-outline-secondary btn-sm lc-toggle-btn"
                              [class.active]="(editing.printSpeed ?? 4) === s"
                              (click)="editing.printSpeed = s">{{ s }} ips</button>
                    </div>
                  </div>
                  <p class="lc-hint">4 ips is default. Lower = better quality; higher = faster.</p>
                </div>
              </ng-container>

              <!-- Label Dimensions -->
              <div class="lc-section">
                <div class="lc-section-header">Label Dimensions</div>

                <div class="lc-slider-row">
                  <div class="lc-slider-label">
                    <span>Width</span>
                    <strong class="lc-slider-value">{{ editing.labelWidth }} mm</strong>
                  </div>
                  <input type="range" class="form-range" min="30" max="150" step="1"
                         [(ngModel)]="editing.labelWidth">
                  <div class="lc-slider-bounds"><span>30 mm</span><span>150 mm</span></div>
                </div>

                <ng-container *ngIf="editing.pageSize === 'die-cut' || editing.printerType === 'zebra'">
                  <div class="lc-slider-row mt-3">
                    <div class="lc-slider-label">
                      <span>Height</span>
                      <strong class="lc-slider-value">{{ editing.labelHeight }} mm</strong>
                    </div>
                    <input type="range" class="form-range" min="10" max="150" step="1"
                           [(ngModel)]="editing.labelHeight">
                    <div class="lc-slider-bounds"><span>10 mm</span><span>150 mm</span></div>
                  </div>

                  <div class="lc-slider-row mt-3">
                    <div class="lc-slider-label">
                      <span>Inter-label Gap</span>
                      <strong class="lc-slider-value">{{ editing.labelGap }} mm</strong>
                    </div>
                    <input type="range" class="form-range" min="0" max="10" step="0.5"
                           [(ngModel)]="editing.labelGap">
                    <div class="lc-slider-bounds"><span>0 mm</span><span>10 mm</span></div>
                  </div>
                  <p class="lc-hint">Pitch (label + gap) = <strong>{{ editing.labelHeight + editing.labelGap }} mm</strong></p>
                </ng-container>

                <div class="lc-slider-row mt-3">
                  <div class="lc-slider-label">
                    <span>Top Padding</span>
                    <strong class="lc-slider-value">{{ (editing.topPaddingMm ?? 1.5).toFixed(1) }} mm</strong>
                  </div>
                  <input type="range" class="form-range" min="0" max="10" step="0.5"
                         [ngModel]="editing.topPaddingMm ?? 1.5"
                         (ngModelChange)="editing.topPaddingMm = +$event">
                  <div class="lc-slider-bounds"><span>0 mm</span><span>10 mm</span></div>
                </div>
                <p class="lc-hint">Space at the top of the label before the first line of content.</p>
              </div>

              <!-- Fields on Label -->
              <div class="lc-section">
                <div class="lc-section-header">
                  Fields on Label
                  <span class="lc-section-hint">— horizontal &nbsp; ↓↑ vertical</span>
                </div>

                <div class="lc-fields">

                  <!-- Book Title -->
                  <div class="lc-field-card" [class.lc-field-card--on]="editing.showTitle">
                    <label class="lc-field-card__check">
                      <input type="checkbox" [(ngModel)]="editing.showTitle">
                      <span>Book Title</span>
                    </label>
                    <div class="lc-field-card__orient" *ngIf="editing.showTitle">
                      <div class="lc-orient-btns">
                        <button type="button" class="lc-orient-btn"
                                [class.active]="(editing.titleOrientation || 'H') === 'H'"
                                (click)="editing.titleOrientation = 'H'" title="Horizontal">—</button>
                        <button type="button" class="lc-orient-btn"
                                [class.active]="editing.titleOrientation === 'VT'"
                                (click)="editing.titleOrientation = 'VT'" title="Vertical top→bottom">↓</button>
                        <button type="button" class="lc-orient-btn"
                                [class.active]="editing.titleOrientation === 'VB'"
                                (click)="editing.titleOrientation = 'VB'" title="Vertical bottom→top">↑</button>
                      </div>
                      <div class="lc-orient-btns lc-orient-btns--loc" *ngIf="isV(editing.titleOrientation)">
                        <button type="button" class="lc-orient-btn lc-orient-btn--loc"
                                [class.active]="(editing.titleLocation || 'left') === 'left'"
                                (click)="editing.titleLocation = 'left'">L</button>
                        <button type="button" class="lc-orient-btn lc-orient-btn--loc"
                                [class.active]="editing.titleLocation === 'right'"
                                (click)="editing.titleLocation = 'right'">R</button>
                      </div>
                    </div>
                  </div>

                  <!-- Author Name -->
                  <div class="lc-field-card" [class.lc-field-card--on]="editing.showAuthor">
                    <label class="lc-field-card__check">
                      <input type="checkbox" [(ngModel)]="editing.showAuthor">
                      <span>Author Name</span>
                    </label>
                    <div class="lc-field-card__orient" *ngIf="editing.showAuthor">
                      <div class="lc-orient-btns">
                        <button type="button" class="lc-orient-btn"
                                [class.active]="(editing.authorOrientation || 'H') === 'H'"
                                (click)="editing.authorOrientation = 'H'">—</button>
                        <button type="button" class="lc-orient-btn"
                                [class.active]="editing.authorOrientation === 'VT'"
                                (click)="editing.authorOrientation = 'VT'">↓</button>
                        <button type="button" class="lc-orient-btn"
                                [class.active]="editing.authorOrientation === 'VB'"
                                (click)="editing.authorOrientation = 'VB'">↑</button>
                      </div>
                      <div class="lc-orient-btns lc-orient-btns--loc" *ngIf="isV(editing.authorOrientation)">
                        <button type="button" class="lc-orient-btn lc-orient-btn--loc"
                                [class.active]="(editing.authorLocation || 'left') === 'left'"
                                (click)="editing.authorLocation = 'left'">L</button>
                        <button type="button" class="lc-orient-btn lc-orient-btn--loc"
                                [class.active]="editing.authorLocation === 'right'"
                                (click)="editing.authorLocation = 'right'">R</button>
                      </div>
                    </div>
                  </div>

                  <!-- Library / Center Name -->
                  <div class="lc-field-card" [class.lc-field-card--on]="editing.showLibrary">
                    <label class="lc-field-card__check">
                      <input type="checkbox" [(ngModel)]="editing.showLibrary">
                      <span>Library / Center Name</span>
                    </label>
                    <div class="lc-field-card__orient" *ngIf="editing.showLibrary">
                      <div class="lc-orient-btns">
                        <button type="button" class="lc-orient-btn"
                                [class.active]="(editing.libraryOrientation || 'H') === 'H'"
                                (click)="editing.libraryOrientation = 'H'">—</button>
                        <button type="button" class="lc-orient-btn"
                                [class.active]="editing.libraryOrientation === 'VT'"
                                (click)="editing.libraryOrientation = 'VT'">↓</button>
                        <button type="button" class="lc-orient-btn"
                                [class.active]="editing.libraryOrientation === 'VB'"
                                (click)="editing.libraryOrientation = 'VB'">↑</button>
                      </div>
                      <div class="lc-orient-btns lc-orient-btns--loc" *ngIf="isV(editing.libraryOrientation)">
                        <button type="button" class="lc-orient-btn lc-orient-btn--loc"
                                [class.active]="(editing.libraryLocation || 'left') === 'left'"
                                (click)="editing.libraryLocation = 'left'">L</button>
                        <button type="button" class="lc-orient-btn lc-orient-btn--loc"
                                [class.active]="editing.libraryLocation === 'right'"
                                (click)="editing.libraryLocation = 'right'">R</button>
                      </div>
                    </div>
                  </div>

                  <!-- Barcode + Sr.No. -->
                  <div class="lc-field-card lc-field-card--fixed">
                    <div class="lc-field-card__check lc-field-card__check--fixed">
                      <i class="pe-7s-barcode lc-field-icon"></i>
                      <span>Barcode + Sr.No.</span>
                      <span class="lc-field-always">always</span>
                    </div>
                    <div class="lc-field-card__orient">
                      <div class="lc-orient-btns">
                        <button type="button" class="lc-orient-btn"
                                [class.active]="(editing.barcodeOrientation || 'H') === 'H'"
                                (click)="editing.barcodeOrientation = 'H'">—</button>
                        <button type="button" class="lc-orient-btn"
                                [class.active]="editing.barcodeOrientation === 'VT'"
                                (click)="editing.barcodeOrientation = 'VT'">↓</button>
                        <button type="button" class="lc-orient-btn"
                                [class.active]="editing.barcodeOrientation === 'VB'"
                                (click)="editing.barcodeOrientation = 'VB'">↑</button>
                      </div>
                      <div class="lc-orient-btns lc-orient-btns--loc" *ngIf="isV(editing.barcodeOrientation)">
                        <button type="button" class="lc-orient-btn lc-orient-btn--loc"
                                [class.active]="(editing.barcodeLocation || 'left') === 'left'"
                                (click)="editing.barcodeLocation = 'left'">L</button>
                        <button type="button" class="lc-orient-btn lc-orient-btn--loc"
                                [class.active]="editing.barcodeLocation === 'right'"
                                (click)="editing.barcodeLocation = 'right'">R</button>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              <!-- Text Sizes -->
              <div class="lc-section">
                <div class="lc-section-header">Text Sizes</div>
                <p class="lc-hint mb-3">Font height per field — applies to both ZPL and browser print.</p>

                <ng-container *ngIf="editing.showTitle">
                  <div class="lc-slider-row">
                    <div class="lc-slider-label">
                      <span>Book Title</span>
                      <strong class="lc-slider-value">{{ (editing.titleFontSizeMm ?? 2.2).toFixed(1) }} mm</strong>
                    </div>
                    <input type="range" class="form-range" min="1.5" max="6" step="0.1"
                           [ngModel]="editing.titleFontSizeMm ?? 2.2"
                           (ngModelChange)="editing.titleFontSizeMm = +$event">
                    <div class="lc-slider-bounds"><span>1.5 mm</span><span>6 mm</span></div>
                  </div>
                </ng-container>

                <ng-container *ngIf="editing.showAuthor">
                  <div class="lc-slider-row mt-3">
                    <div class="lc-slider-label">
                      <span>Author Name</span>
                      <strong class="lc-slider-value">{{ (editing.authorFontSizeMm ?? 1.8).toFixed(1) }} mm</strong>
                    </div>
                    <input type="range" class="form-range" min="1.5" max="6" step="0.1"
                           [ngModel]="editing.authorFontSizeMm ?? 1.8"
                           (ngModelChange)="editing.authorFontSizeMm = +$event">
                    <div class="lc-slider-bounds"><span>1.5 mm</span><span>6 mm</span></div>
                  </div>
                </ng-container>

                <ng-container *ngIf="editing.showLibrary">
                  <div class="lc-slider-row mt-3">
                    <div class="lc-slider-label">
                      <span>Library / Center</span>
                      <strong class="lc-slider-value">{{ (editing.libraryFontSizeMm ?? 1.8).toFixed(1) }} mm</strong>
                    </div>
                    <input type="range" class="form-range" min="1.5" max="6" step="0.1"
                           [ngModel]="editing.libraryFontSizeMm ?? 1.8"
                           (ngModelChange)="editing.libraryFontSizeMm = +$event">
                    <div class="lc-slider-bounds"><span>1.5 mm</span><span>6 mm</span></div>
                  </div>
                </ng-container>

                <div class="lc-slider-row mt-3">
                  <div class="lc-slider-label">
                    <span>Serial Number</span>
                    <strong class="lc-slider-value">{{ (editing.srnoFontSizeMm ?? 2.0).toFixed(1) }} mm</strong>
                  </div>
                  <input type="range" class="form-range" min="1.5" max="6" step="0.1"
                         [ngModel]="editing.srnoFontSizeMm ?? 2.0"
                         (ngModelChange)="editing.srnoFontSizeMm = +$event">
                  <div class="lc-slider-bounds"><span>1.5 mm</span><span>6 mm</span></div>
                </div>
              </div>

              <!-- Barcode -->
              <div class="lc-section lc-section--last">
                <div class="lc-section-header">Barcode</div>

                <div class="lc-slider-row">
                  <div class="lc-slider-label">
                    <span>Bar Height</span>
                    <strong class="lc-slider-value">
                      <ng-container *ngIf="!((editing.barcodeHeightMm ?? 0) > 0)">Auto</ng-container>
                      <ng-container *ngIf="(editing.barcodeHeightMm ?? 0) > 0">{{ editing.barcodeHeightMm }} mm</ng-container>
                    </strong>
                  </div>
                  <input type="range" class="form-range" min="0" max="50" step="1"
                         [ngModel]="editing.barcodeHeightMm ?? 0"
                         (ngModelChange)="editing.barcodeHeightMm = +$event">
                  <div class="lc-slider-bounds"><span>0 (auto)</span><span>50 mm</span></div>
                </div>
                <p class="lc-hint"><strong>0 = auto</strong> — fills space after text. Min 5 mm enforced.</p>

                <div class="lc-inline-group mt-3">
                  <span class="lc-inline-label">Bar Width</span>
                  <div class="btn-group" role="group">
                    <button type="button" class="btn btn-outline-secondary btn-sm lc-toggle-btn"
                            [class.active]="(editing.barcodeModuleWidth ?? 2) === 1"
                            (click)="editing.barcodeModuleWidth = 1">Narrow</button>
                    <button type="button" class="btn btn-outline-secondary btn-sm lc-toggle-btn"
                            [class.active]="(editing.barcodeModuleWidth ?? 2) === 2"
                            (click)="editing.barcodeModuleWidth = 2">Standard</button>
                    <button type="button" class="btn btn-outline-secondary btn-sm lc-toggle-btn"
                            [class.active]="(editing.barcodeModuleWidth ?? 2) === 3"
                            (click)="editing.barcodeModuleWidth = 3">Wide</button>
                  </div>
                </div>
                <p class="lc-hint">All three produce valid CODE128. Wider bars need a wider label.</p>
              </div>

            </div><!-- /lc-form -->

            <!-- ── Preview column ───────────────────────────────── -->
            <div class="lc-preview">

              <div class="lc-preview-header">Live Preview</div>

              <div class="lc-preview-stage">
                <div class="lc-label-mock"
                     [style.width.px]="previewPx"
                     [style.height.px]="previewHeightPx"
                     [class.lc-label-mock--fixed]="editing.pageSize === 'die-cut'"
                     [class.lc-label-mock--cols]="previewHasLeft || previewHasRight">

                  <!-- Left side column -->
                  <div class="lc-mock-col-v" *ngIf="previewHasLeft">
                    <ng-container *ngIf="editing.showTitle && isV(editing.titleOrientation) && (editing.titleLocation || 'left') === 'left'">
                      <div class="lcp-title"
                           [ngStyle]="getOrientStyle(editing.titleOrientation)"
                           [style.font-size.px]="lpx(editing.titleFontSizeMm, 2.2)">Dune</div>
                    </ng-container>
                    <ng-container *ngIf="editing.showAuthor && isV(editing.authorOrientation) && (editing.authorLocation || 'left') === 'left'">
                      <div class="lcp-author"
                           [ngStyle]="getOrientStyle(editing.authorOrientation)"
                           [style.font-size.px]="lpx(editing.authorFontSizeMm, 1.8)">Frank Herbert</div>
                    </ng-container>
                    <ng-container *ngIf="isV(editing.barcodeOrientation) && (editing.barcodeLocation || 'left') === 'left'">
                      <div class="lcp-barcode" [ngStyle]="getLcpBarcodeStyle(editing.barcodeOrientation)"></div>
                      <div class="lcp-srno"
                           [ngStyle]="getOrientStyle(editing.barcodeOrientation)"
                           [style.font-size.px]="lpx(editing.srnoFontSizeMm, 2.0)">000042</div>
                    </ng-container>
                    <ng-container *ngIf="editing.showLibrary && isV(editing.libraryOrientation) && (editing.libraryLocation || 'left') === 'left'">
                      <div class="lcp-library"
                           [ngStyle]="getOrientStyle(editing.libraryOrientation)"
                           [style.font-size.px]="lpx(editing.libraryFontSizeMm, 1.8)">{{ resolveLabelName() || 'Lib' }}</div>
                    </ng-container>
                  </div>

                  <!-- Center column -->
                  <div [class.lc-mock-col-h]="previewHasLeft || previewHasRight">
                    <div *ngIf="editing.showTitle && !isV(editing.titleOrientation)"
                         class="lcp-title"
                         [style.font-size.px]="lpx(editing.titleFontSizeMm, 2.2)">Dune</div>
                    <div *ngIf="editing.showAuthor && !isV(editing.authorOrientation)"
                         class="lcp-author"
                         [style.font-size.px]="lpx(editing.authorFontSizeMm, 1.8)">Frank Herbert</div>
                    <ng-container *ngIf="!isV(editing.barcodeOrientation)">
                      <div class="lcp-barcode" [style.height.px]="previewBarcodePx"></div>
                      <div class="lcp-srno" [style.font-size.px]="lpx(editing.srnoFontSizeMm, 2.0)">000042</div>
                    </ng-container>
                    <div *ngIf="editing.showLibrary && !isV(editing.libraryOrientation)"
                         class="lcp-library"
                         [style.font-size.px]="lpx(editing.libraryFontSizeMm, 1.8)">{{ resolveLabelName() || 'Library Name' }}</div>
                  </div>

                  <!-- Right side column -->
                  <div class="lc-mock-col-v" *ngIf="previewHasRight">
                    <ng-container *ngIf="editing.showTitle && isV(editing.titleOrientation) && editing.titleLocation === 'right'">
                      <div class="lcp-title"
                           [ngStyle]="getOrientStyle(editing.titleOrientation)"
                           [style.font-size.px]="lpx(editing.titleFontSizeMm, 2.2)">Dune</div>
                    </ng-container>
                    <ng-container *ngIf="editing.showAuthor && isV(editing.authorOrientation) && editing.authorLocation === 'right'">
                      <div class="lcp-author"
                           [ngStyle]="getOrientStyle(editing.authorOrientation)"
                           [style.font-size.px]="lpx(editing.authorFontSizeMm, 1.8)">Frank Herbert</div>
                    </ng-container>
                    <ng-container *ngIf="isV(editing.barcodeOrientation) && editing.barcodeLocation === 'right'">
                      <div class="lcp-barcode" [ngStyle]="getLcpBarcodeStyle(editing.barcodeOrientation)"></div>
                      <div class="lcp-srno"
                           [ngStyle]="getOrientStyle(editing.barcodeOrientation)"
                           [style.font-size.px]="lpx(editing.srnoFontSizeMm, 2.0)">000042</div>
                    </ng-container>
                    <ng-container *ngIf="editing.showLibrary && isV(editing.libraryOrientation) && editing.libraryLocation === 'right'">
                      <div class="lcp-library"
                           [ngStyle]="getOrientStyle(editing.libraryOrientation)"
                           [style.font-size.px]="lpx(editing.libraryFontSizeMm, 1.8)">{{ resolveLabelName() || 'Lib' }}</div>
                    </ng-container>
                  </div>

                </div>
              </div>

              <!-- Dimensions -->
              <div class="lc-preview-meta">
                <ng-container *ngIf="editing.printerType === 'zebra'">
                  <span class="badge bg-dark me-1" style="font-size:0.65rem; vertical-align:middle;">ZPL</span>
                  {{ editing.labelWidth }}mm × {{ editing.labelHeight }}mm · {{ editing.dpi || 203 }} DPI
                </ng-container>
                <ng-container *ngIf="editing.printerType !== 'zebra'">
                  <ng-container *ngIf="editing.pageSize === 'A4'">{{ editing.labelWidth }}mm wide · A4 sheet</ng-container>
                  <ng-container *ngIf="editing.pageSize === 'thermal'">{{ editing.labelWidth }}mm wide · Thermal roll</ng-container>
                  <ng-container *ngIf="editing.pageSize === 'die-cut'">
                    {{ editing.labelWidth }}mm × {{ editing.labelHeight }}mm · {{ editing.labelGap }}mm gap · pitch {{ editing.labelHeight + editing.labelGap }}mm
                  </ng-container>
                </ng-container>
              </div>

              <!-- Print Sample -->
              <button type="button" class="btn btn-outline-primary btn-sm w-100 mt-2" (click)="printSample()">
                <i class="pe-7s-print me-1"></i>Print Sample Label
              </button>
              <p class="lc-hint text-center mt-1">Prints one sample using current settings</p>

            </div><!-- /lc-preview -->

          </div><!-- /lc-content -->

          <!-- ── Alerts ─────────────────────────────────────────── -->
          <div class="lc-alert-area" *ngIf="choosingNewDefault || errorMessage || successMessage">
            <div class="alert alert-warning mb-0" *ngIf="choosingNewDefault">
              <div class="fw-semibold mb-1">
                <i class="pe-7s-attention me-1"></i>
                "<strong>{{ layouts[pendingDeleteIdx]?.name }}</strong>" is the default layout.
                Select a new default before deleting:
              </div>
              <select class="form-select form-select-sm mt-2" style="max-width:300px;"
                      [(ngModel)]="newDefaultIdx">
                <option *ngFor="let l of remainingLayouts; let i = index" [ngValue]="i">{{ l.name }}</option>
              </select>
              <div class="d-flex gap-2 mt-2">
                <button type="button" class="btn btn-danger btn-sm"
                        (click)="confirmDeleteWithNewDefault()" [disabled]="isSaving">
                  <span *ngIf="isSaving" class="spinner-border spinner-border-sm me-1"></span>
                  Delete &amp; Set New Default
                </button>
                <button type="button" class="btn btn-outline-secondary btn-sm"
                        (click)="cancelDeleteChoice()" [disabled]="isSaving">Cancel</button>
              </div>
            </div>
            <div class="alert alert-danger py-2 px-3 small mb-0" *ngIf="errorMessage">
              <i class="pe-7s-attention me-1"></i>{{ errorMessage }}
            </div>
            <div class="alert alert-success py-2 px-3 small mb-0" *ngIf="successMessage">
              <i class="pe-7s-check me-1"></i>{{ successMessage }}
            </div>
          </div>

        </ng-container>
      </div><!-- /card-body -->

    </div>
  </div>
</div>
